/**
 * "White wedding hall": a plausible starting layout for the 5 Dec white wedding
 * at the Armed Forces Event Centre, built from the decorator's concept drawing.
 * It is a starting point to edit and is NOT to scale. Pure data: nothing here
 * touches the database. The editor adds these rooms to the open floor plan and
 * they are saved only when the planner saves, through the normal save path.
 *
 * Plan view, stage at the top (far end), main entrance at the bottom:
 *   - semicircular four-tier stage and a round dance floor on the centre axis
 *   - 13 crescent tables of 8 (104 VIP seats) around the dance floor, hollow
 *     side toward the stage
 *   - 7 long tables of 16 (112 seats) behind, staggered, with a clear aisle
 *   - four sections A to D (2 x 2), four bars, rope dividers, doors, VIP entrance
 */
import type { Room } from '../../types/Planner';
import { defaultTableSize } from './tableGeometry';

export type PresetRoom = Omit<Room, 'id'>;

export const WHITE_WEDDING_NOTE = 'STARTING POINT - NOT TO SCALE';

const HALL = { x: 160, y: 40, width: 1080, height: 820 };
const AXIS_X = HALL.x + HALL.width / 2; // 700
const SPLIT_Y = 500;                     // front (stage side) / back sections

function zone(type: string, name: string, x: number, y: number, width: number, height: number, extra: Partial<PresetRoom> = {}): PresetRoom {
    return { name, type: type as Room['type'], position: { x, y }, width, height, ...extra };
}

function sections(): PresetRoom[] {
    const halfW = HALL.width / 2;
    const backH = HALL.y + HALL.height - SPLIT_Y;
    const frontH = SPLIT_Y - HALL.y;
    return [
        zone('section', 'A', HALL.x, HALL.y, halfW, frontH),
        zone('section', 'B', AXIS_X, HALL.y, halfW, frontH),
        zone('section', 'C', HALL.x, SPLIT_Y, halfW, backH),
        zone('section', 'D', AXIS_X, SPLIT_Y, halfW, backH),
    ];
}

function tables(): PresetRoom[] {
    const out: PresetRoom[] = [];
    let n = 1;
    const crescent = defaultTableSize('crescent');
    const crescentRow = (x: number, y: number) =>
        zone('table', String(n++), x, y, crescent.width, crescent.height, { tableShape: 'crescent', seats: 8 });
    const mirrorX = (x: number, w: number) => 2 * AXIS_X - x - w;

    // 13 crescents: three rows each side of the dance floor, one on the axis.
    const rowsY = [205, 300, 395];
    const leftXs = [225, 395];
    for (const y of rowsY) {
        for (const x of leftXs) out.push(crescentRow(x, y));
        for (const x of leftXs) out.push(crescentRow(mirrorX(x, crescent.width), y));
    }
    out.push(crescentRow(AXIS_X - crescent.width / 2, 205));

    // 7 long tables of 16, staggered behind a clear aisle.
    const long = defaultTableSize('rectangular', 16);
    const longTable = (x: number, y: number) =>
        zone('table', String(n++), x, y, long.width, long.height, { tableShape: 'rectangular', seats: 16 });
    const leftRow1 = [185, 415];
    for (const x of leftRow1) out.push(longTable(x, 540));
    for (const x of leftRow1) out.push(longTable(mirrorX(x, long.width), 540));
    out.push(longTable(300, 640));
    out.push(longTable(770, 640));
    out.push(longTable(1010, 640));
    return out;
}

function elements(): PresetRoom[] {
    return [
        zone('platform', 'Low platform 1', 300, 805, 220, 40),
        zone('platform', 'Low platform 2', 880, 805, 220, 40),
        zone('divider', 'Rope divider 1', HALL.x, SPLIT_Y - 6, 480, 12),
        zone('divider', 'Rope divider 2', AXIS_X + 60, SPLIT_Y - 6, 480, 12),
        zone('divider', 'Rope divider 3', 640, SPLIT_Y + 6, 12, 300),
        zone('divider', 'Rope divider 4', 748, SPLIT_Y + 6, 12, 300),
        zone('bar', 'Bar 1', 170, 330, 36, 110),
        zone('bar', 'Bar 2', 1194, 330, 36, 110),
        zone('bar', 'Bar 3', 170, 730, 36, 110),
        zone('bar', 'Bar 4', 1194, 730, 36, 110),
        zone('stage_tiered', 'Stage', AXIS_X - 180, 50, 360, 130),
        zone('dance_round', 'Dance floor', AXIS_X - 85, 300, 170, 170),
        zone('vip_entrance', 'VIP entrance', 10, 100, 140, 36),
        zone('activity_tent', 'Activity tent (VIP)', 10, 160, 140, 100),
        zone('photo_spot', 'Photo spot 1', 180, 60, 70, 60),
        zone('photo_spot', 'Photo spot 2', 500, 875, 70, 60),
        zone('aides_tent', 'Aides tent', 1250, 160, 130, 90),
        zone('souvenir_room', 'Exit and souvenir room', 1040, 870, 120, 80),
        zone('door', 'Door 1', AXIS_X - 20, 846, 40, 14),
        zone('door', 'Door 2', 153, 500, 14, 40),
        zone('door', 'Door 3', 1233, 500, 14, 40),
        zone('door', 'Door 4', 153, 100, 14, 40),
        zone('door', 'Door 5', 1233, 100, 14, 40),
        zone('door', 'Door 6', 560, 846, 40, 14),
    ];
}

/** Every room of the preset, back to front (sortOrder follows the array). */
export function buildWhiteWeddingHall(): PresetRoom[] {
    const hall = zone('reception', 'Main hall', HALL.x, HALL.y, HALL.width, HALL.height, { locked: true });
    const note = zone('other', WHITE_WEDDING_NOTE, HALL.x, 4, 420, 28, { locked: true });
    return [hall, ...sections(), ...elements(), ...tables(), note]
        .map((r, i) => ({ ...r, sortOrder: i }));
}
